import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import ActionButton from "components/Buttons/ActionButton";
import { EditButton } from "components/Buttons/EditButton";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import StatusToggle from "components/StatusToggle";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  exportLabourMasterCSV,
  fetchAllLabours,
  updateStatusInLabour,
} from "slices/Labour/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";

const selectLabours = createSelector(
  (state: any) => state.labour,
  (labour) => ({
    labours: labour.labourList,
    rowCount: labour.rowCount,
    loading: labour.loading,
    error: labour.error,
  })
);

const Labour = () => {
  document.title = "Labour List";
  const dispatch = useDispatch<any>();
  const location = useLocation();

  // Redux selector for labour state
  const { labours, rowCount, loading, error } = useSelector(selectLabours);

  // Use custom hook to fetch and access permissions
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.master?.app_name,
    app_modules?.master?.modules?.labour
  );

  // Use preserved table state hook
  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = usePreservedTableState({
    key: "labour",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const [data, setData] = useState([]);
  const [isStatusUpdateLoading, setIsStatusUpdateLoading] =
    useState<boolean>(false);

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  useEffect(() => {
    dispatch(
      fetchAllLabours({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    filterValue,
  ]);

  useEffect(() => {
    setData(labours);
  }, [labours]);

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  useEffect(() => {
    setData(labours);
  }, [labours]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const trimmedInput = searchInput.trim();

      const dateMatch = trimmedInput.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        const isoDate = `${year}-${month}-${day}`;
        setSearch(isoDate);
      } else {
        setSearch(trimmedInput);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch]);

  const handleStatusToggle = useCallback(
    async (data: any) => {
      setIsStatusUpdateLoading(true);
      try {
        const updatedStatus = !data.is_active;

        await dispatch(
          updateStatusInLabour({
            labour_id: data.labour_id,
            is_active: updatedStatus,
          })
        ).unwrap();

        toast.success(
          `Labour ${updatedStatus ? "activated" : "deactivated"} successfully`
        );

        dispatch(
          fetchAllLabours({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: filterValue,
          })
        );
      } catch (error: any) {
        const errorMsg =
          error?.error ||
          error?.message ||
          error?.response?.data?.error ||
          "Failed to update status";
        toast.error(errorMsg);
      } finally {
        setIsStatusUpdateLoading(false);
      }
    },
    [
      dispatch,
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      filterValue,
    ]
  );

  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      await exportLabourMasterCSV({
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      });

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Labour export completed successfully.",
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export Labour data";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  };

  const DynamicAction = useMemo(() => {
    if (!canUpdate) {
      return [];
    }

    return [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          // <ul className="d-flex gap-2 list-unstyled mb-0">
          //   <li>
          //     <Link
          //       to={`/edit-labour/${row.original.labour_id}`}
          //       className="btn btn-subtle-secondary btn-icon btn-sm edit-item-btn"
          //       // onClick={() => handleEdit(row.original)}
          //     >
          //       <i className="ph-pencil"></i>
          //     </Link>
          //   </li>
          // </ul>
          <EditButton to={`/edit-labour/${row.original.labour_id}`} />
        ),
      },
    ];
  }, [canUpdate]);

  // : [];

  const columns = useMemo(
    () => [
      {
        header: "Sr. No.",
        id: "sr_no", // important: since there's no accessorKey
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: any }) => {
          return (
            row.original.sr_no || row.index + 1 + pageIndex * pageSize || "-"
          );
        },
      },
      {
        header: "Labour Name",
        accessorKey: "labour_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        Rnet: "labour_name",
        filterFetchUrl: "/master/labour/dropdown/labour_name",
        // cell: ({ row }: { row: any }) => {
        //   const address = row.original.labour_name?.trim();
        //   return address ? address : "-";
        // },
      },

      {
        header: "Contact Number",
        accessorKey: "contact_number",
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "dropdown" as const,
        // cell: ({ row }: { row: any }) => {
        //   const contact_number = row.original.contact_number?.trim();
        //   return contact_number ? contact_number : "-";
        // },
      },

      {
        header: "Status",
        accessorKey: "is_active",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const data = row.original;
          return (
            <StatusToggle
              isActive={data.is_active}
              onChange={() => {
                handleStatusToggle(data);
              }}
              disabled={!canUpdate}
            />
          );
        },
      },
      {
        header: "Remarks",
        id: "remark", // used `id` instead of accessorKey since data isn't ready yet
        accessorKey: "remark", //*// use Once API starts returning a remark field
        enableSorting: false,
        enableColumnFilter: false,
        maxSize: 250,
        cell: ({ row }: { row: any }) => (
          <TruncatedTextCell
            text={row?.original?.remark}
            maxWidth="300px"
            maxLines={4}
            rowIndex={row.index}
            tooltipId={`remarks-tooltip-${row.index}`}
          />
        ),
      },
      {
        header: "Address",
        accessorKey: "address",
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => (
          <TruncatedTextCell
            text={row?.original?.address}
            maxWidth="300px"
            maxLines={4}
            rowIndex={row.index}
            tooltipId={`address-tooltip-${row.index}`}
          />
        ),
      },
      {
        header: "State",
        accessorKey: "state",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        Rnet: "state",
        filterFetchUrl: "/master/labour/dropdown/state",
        // cell: ({ row }: { row: any }) => {
        //   const address = row.original.address?.trim();
        //   return address ? address : "-";
        // },
      },

      {
        header: "City",
        accessorKey: "city",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        Rnet: "city", //*// This is important
        filterFetchUrl: "/master/labour/dropdown/city",
        cell: ({ row }: { row: any }) => row.original.city || "-",
      },
      {
        header: "Country",
        accessorKey: "country",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        Rnet: "country", //*// This is important
        filterFetchUrl: "/master/labour/dropdown/country",
        cell: ({ row }: { row: any }) => row.original.country || "-",
      },
      {
        header: "Pincode",
        accessorKey: "pincode",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => row.original.pincode || "-",
      },

      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user.username ?? "N/A";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "N/A";
          return `${name} ${date}`;
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user.username ?? "N/A";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "N/A";
          return `${name} ${date}`;
        },
      },

      ...DynamicAction,
    ],
    [pageIndex, pageSize, canUpdate, DynamicAction, handleStatusToggle]
  );

  return (
    <ListingPageContainer>
      <style>
        {`
          .truncated-text-cell-wrapper {
            min-width: 0 !important;
            display: -webkit-box !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            white-space: normal !important;
            line-height: 1.5em !important;
          }
        `}
      </style>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Labour</h6>
                </Col>

                {/* Search Input Box (reusable)*/}
                <Col xl={2} md={3}>
                  <SearchBox value={searchInput} onChange={setSearchInput} />
                </Col>

                <Col className="col-md-auto">
                  <div className="hstack gap-2">
                    {canView && (
                      <ActionButton
                        label="Export"
                        icon="bi bi-box-arrow-up"
                        handleClick={handleExport}
                        variant="light"
                      />
                    )}

                    {canCreate && (
                      <ActionButton
                        label="Add Labour"
                        icon="bi bi-plus-circle"
                        navigateLink="/add-labour"
                        variant="primary"
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper
                loading={loading || isStatusUpdateLoading}
                error={error}
              >
                <TableContainer
                  columns={columns}
                  data={data}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  SearchPlaceholder="Search labours..."
                  manualSorting={true}
                  colValue={setFilterValue}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default Labour;
